import * as React from 'react';

import * as api from '@gitbook/api-client';
import { Logger } from '@gitbook/util-debug';
import { assertIsDefined } from '@gitbook/util-ts';

import type { StepId, StepperType } from '../common';

const logger = Logger('site-template');

/**
 * How to update the site template.
 *
 * The idea is to have Design export the combined SVG of all site plans into one single file.
 * The SVG can be then converted into a react component with a tool like:
 *
 *   https://www.svgviewer.dev/svg-to-react-jsx
 *
 * Or any other mean to convert the SVG to a React component without losing the IDs.
 *
 * We should not need to touch the resulting component, apart from renaming it. So it will be
 * easier to make changes.
 *
 * Design can give us a new SVG and we can just convert it to react, rename it and update the
 * logic below if necessary. E.g. in case some new UI with new IDs has been added.
 *
 * The SVG comes with dynamic sections and elements identified by predefined IDs that we agreed
 * upon with Design. The useSiteTemplateState hook contains all the logic to make the SVG
 * dynamically react to the user input.
 */

enum SVGElementID {
    // Displayed on top of the Ultimate site, representing VA
    Login = 'login',

    // The url in the browser address bar
    Url = 'url',

    // The header for Basic sites
    HeaderBasic = 'headerBasic',

    // The header for Paid sites
    HeaderPaid = 'headerPaid',

    // The ads element for the Sponsored site
    Advert = 'advert',

    // Site sections for Ultimate sites, displayed under the header
    Sections = 'sections',

    // The sidebar element displayed on the left side of the site, with one item
    Sidebar = 'sidebar',

    // All the other items of the sidebar element
    additionalContent = 'additionalContent',

    // The background of the sidebar element, for Paid sites
    SidebarFill = 'sidebarFill',

    // The TOC element displayed on the right side of the site
    TableOfContents = 'tableOfContents',

    // The page ratings, displayed on the right side of the site
    PageRating = 'pageRating',

    // The emoji displayed in the Basic site header
    Emoji = 'emoji',

    // The site title displayed in the Basic site header
    Title = 'title',

    // The content part of the site for the name step
    ContentName = 'contentName',

    // The content part of the site for the content step
    ContentContent = 'contentContent',

    // The content part of the site for the features step
    ContentFeatures = 'contentFeatures',

    // The content part of the site for the publish step
    ContentPublish = 'contentPublish',

    // The content part of the site for the publish step
    PoweredBy = 'poweredBy',
}

// This is the height of the Site Sections element, as agreed with Design
const SiteSectionsElementHeight = 33;

// List of emojis to display in the header.
const emojis = ['🚀', '😎', '🔥', '🌟', '💡', '🎉', '👍', '🌈', '🍕', '🎧', '⚡️'];
let currentEmojiIndex = 0;

/**
 * Contains all the logic and state to display the correct template for the given
 * step and site plan.
 */
export function useSiteTemplateState(props: {
    step: StepId<StepperType>;
    name: string;
    currentPlan: api.SiteType | null;
    noContent: boolean;
    publishLater: boolean;
    withVA: boolean;
}) {
    const { step, name, currentPlan, noContent, publishLater, withVA } = props;
    const elementsRef = React.useRef<Record<SVGElementID, HTMLElement> | null>(null);
    const siteTitle = truncateString(name || 'Org Docs', 50);

    const changeEmoji = React.useCallback(() => {
        if (elementsRef.current?.emoji) {
            currentEmojiIndex = (currentEmojiIndex + 1) % emojis.length;
            setEmoji(elementsRef.current, emojis[currentEmojiIndex]);
        }
    }, []);

    React.useEffect(() => {
        try {
            // Get all the elements, once
            if (!elementsRef.current) {
                // Get all elements
                elementsRef.current = Object.fromEntries(
                    Object.values(SVGElementID).map((id) => {
                        const el = document.getElementById(id);
                        assertIsDefined(el, `Element with ${id} id not found in SiteTemplateSVG`);
                        return [id, el];
                    })
                ) as Record<SVGElementID, HTMLElement>;

                // Add emoji element event listener, to change emoji on click
                elementsRef.current.emoji.addEventListener('click', changeEmoji);

                // Make emoji and siblings non-selectable
                elementsRef.current.emoji.parentElement?.style.setProperty('user-select', 'none');

                // Set fonts
                elementsRef.current.title.classList.add('content-heading-small', 'text-[14px]');
                elementsRef.current.advert.classList.add('text-[10px]');
                elementsRef.current.poweredBy.classList.add(
                    'font-medium',
                    'text-[12px]',
                    'text-base'
                );

                // Minor positioning adjustements due to font mismatches
                elementsRef.current.title.querySelector('tspan')?.setAttribute('y', '69');

                // Some animation presets
                elementsRef.current.sidebar.classList.add('transition', 'duration-300');
                elementsRef.current.contentContent.classList.add('transition', 'duration-300');
                elementsRef.current.contentPublish.classList.add('transition', 'duration-300');
            }

            const elements = elementsRef.current;

            // Reset
            toggleVisibilityAll(elements, false);
            toggleSiteSections(elements, false);
            togglePaidHeader(elements, false);

            // Enable basic generic layout (name and publish steps)
            toggleContent(elements, step);
            toggleVisibility(elements.headerBasic, true);
            toggleVisibility(elements.sidebar, true);
            toggleVisibility(elements.additionalContent, true);
            toggleVisibility(elements.tableOfContents, true);
            toggleVisibility(elements.pageRating, true);
            toggleVisibility(elements.poweredBy, true);
            setTextContent(elements.url, 'gitbook.io');
            setTextContent(elements.title, siteTitle);
            setEmoji(elements, emojis[currentEmojiIndex]);

            // Content step overrides
            if (step === 'content') {
                toggleVisibility(elements.tableOfContents, false);
                toggleVisibility(elements.additionalContent, !noContent);
                toggleContent(elements, noContent ? false : step);
            }

            // Publish step overrides
            if (step === 'publish') {
                if (publishLater) {
                    elements.sidebar.classList.add('saturate-0');
                    elements.contentPublish.classList.add('saturate-0');
                } else {
                    elements.sidebar.classList.remove('saturate-0');
                    elements.contentPublish.classList.remove('saturate-0');
                }
            }

            // Features step overrides, per plan
            if (step === 'features') {
                switch (currentPlan) {
                    case api.SiteType.Basic:
                        toggleVisibility(elements.pageRating, false);
                        break;
                    case api.SiteType.Premium:
                        toggleVisibility(elements.pageRating, true);
                        togglePaidHeader(elements, true);
                        toggleVisibility(elements.sidebarFill, true);
                        setTextContent(elements.url, 'docs.org.com');
                        break;

                    case api.SiteType.Ultimate:
                        toggleVisibility(elements.pageRating, true);
                        togglePaidHeader(elements, true);
                        toggleSiteSections(elements, true);
                        toggleVisibility(elements.sidebarFill, true);
                        setTextContent(elements.url, 'docs.org.com');

                        if (withVA) {
                            toggleVisibility(elements.login, true);
                        }

                        // Reduce sidebar height because of sections (35px reduction)
                        elements.sidebarFill.setAttribute(
                            'd',
                            'M40 146C40 141.582 43.5817 138 48 138H204C208.418 138 212 141.582 212 146V561C212 565.418 208.418 569 204 569H48C43.5817 569 40 565.418 40 561V146Z'
                        );

                        // Move the poweredBy up
                        elements.poweredBy
                            .querySelector('path')
                            ?.setAttribute('transform', 'translate(0, -35)');
                        elements.poweredBy.querySelector('tspan')?.setAttribute('y', '547.5');
                        elements.poweredBy.querySelector('rect')?.setAttribute('y', '526.5');
                        break;

                    case api.SiteType.Sponsored:
                        toggleVisibility(elements.pageRating, true);
                        toggleVisibility(elements.advert, true);
                        togglePaidHeader(elements, true);
                        toggleVisibility(elements.sidebarFill, true);
                        setTextContent(elements.url, 'docs.org.com');
                        break;
                }
            }
        } catch (error) {
            // just log to monitor how this goes, if something doesn't work out it's too
            // bad but at least the user can still create site
            logger.warning('error in useSiteTemplateState useEffect', error);
        }
    }, [step, currentPlan, noContent, publishLater, siteTitle, changeEmoji, withVA]);
}

/**
 * Truncate a string to a maximum length.
 */
function truncateString(str: string, maxLength: number) {
    return str.length > maxLength ? `${str.slice(0, maxLength)}...` : str;
}

/**
 * Sets the textContent of the given HTMLElement.
 * If the element has a <tspan> child, sets the text on that, else
 * on the element itself.
 */
function setTextContent(element: HTMLElement, text: string) {
    toggleVisibility(element, true);
    const elementSpan = element.querySelector('tspan');
    if (elementSpan) {
        elementSpan.textContent = text;
    } else {
        element.textContent = text;
    }
}

/**
 * Shows or hides the given element with a fading animation
 */
function toggleVisibility(element: HTMLElement, show: boolean) {
    element.classList.add('fill-mode-forwards');
    if (show) {
        element.classList.remove('animate-out', 'fade-out');
        element.classList.add('animate-in', 'fade-in');
    } else {
        element.classList.remove('animate-in', 'fade-in');
        element.classList.add('animate-out', 'fade-out');
    }
    element.style.opacity = show ? '100' : '0';
}

/**
 * Shows or hides all given elements
 */
function toggleVisibilityAll(elements: Record<SVGElementID, HTMLElement>, show: boolean) {
    Object.values(elements).forEach((element) => toggleVisibility(element, show));
}

/**
 * Toggles betwen the basic and the paid header
 */
function togglePaidHeader(elements: Record<SVGElementID, HTMLElement>, show: boolean) {
    toggleVisibility(elements.headerPaid, show);
    toggleVisibility(elements.headerBasic, !show);
}

/**
 * Shows or hides sites sections and adjusts the vertical position of all elements
 * below them accordingly.
 */
function toggleSiteSections(elements: Record<SVGElementID, HTMLElement>, show: boolean) {
    const elementsToMove = [
        elements.sidebar,
        elements.contentName,
        elements.contentContent,
        elements.contentFeatures,
        elements.contentPublish,
        elements.tableOfContents,
    ];
    if (show) {
        toggleVisibility(elements.sections, true);
        elementsToMove.forEach((element) => {
            element.removeAttribute('transform');
        });
    } else {
        toggleVisibility(elements.sections, false);
        elementsToMove.forEach((element) => {
            element.setAttribute('transform', `translate(0, -${SiteSectionsElementHeight})`);
        });
    }
}

/**
 * Shows the content for the given step. If `false` is passed, hides content.
 */
function toggleContent(
    elements: Record<SVGElementID, HTMLElement>,
    step: StepId<StepperType> | false
) {
    toggleVisibility(elements.contentName, step === 'name');
    toggleVisibility(elements.contentContent, step === 'content');
    toggleVisibility(elements.contentFeatures, step === 'features');
    toggleVisibility(elements.contentPublish, step === 'publish');
}

/**
 * Changes the emoji on the basic template
 */
function setEmoji(elements: Record<SVGElementID, HTMLElement>, newEmoji: string) {
    toggleVisibility(elements.emoji, true);
    setTextContent(elements.emoji, newEmoji);
    elements.emoji.classList.add('cursor-pointer');
}
